import React from "react";
import Image from "@gc-npm/components.c-image";
import Button from "@gc-npm/components.button";
import Price from "@gc-npm/components.price";
import Label from "@gc-npm/components.cms-block.cms-label";
import CustomLink from "@/components/custom-link";
import TextInfo, {
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_SIZE,
} from "@gc-npm/components.text";
import getColor from '@gc-npm/utils.enums.colors';
import DetailCardTitle from "@gc-npm/components.cms-block.cms-detail-card-title";
import DetailCardIconList from "@/components/Recomendador/cms-component-atom-icon-list";

const DETAIL_CARD_CONTENT_TAGGING_INFO = {
  componentName: "DetailCard",
  componentVariation: "all",
};

function truncateText(text, maxLength = 50) {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

const checkIfEmpty = (el) => !(el?.text || el?.price);

const CardContent = ({ detailCardProps, cmsHandler }) => {
  const backgroundColor = detailCardProps?.backgroundColor? getColor(detailCardProps?.backgroundColor): getColor("white");
  const {
    imageSize,
    layout = "default",
    shadow,
    "cms-atom-label-1": detailCardLabel1,
    "cms-atom-price-1": detailCardPrice1,
    "cms-atom-price-2": detailCardPrice2,
    "cms-atom-button-1": detailCardButton1,
    "cms-atom-button-2": detailCardButton2,
    "cms-atom-image-1": detailCardImage1,
    "cms-atom-text-1": detailCardText1,
    "cms-atom-text-2": detailCardText2,
    "cms-atom-text-3": detailCardText3,
    "cms-atom-text-4": detailCardText4,
    "cms-atom-icon-list-1": detailCardIconList1,
    "cms-ml-detail-card-title-1": detailCardTitle,
    "cms-chidlrens": cmsChildrens,
    loaded = true,
    className = "",
  } = detailCardProps;

  const cmsChildren1 = cmsChildrens?.["cms-children-1"];
  const cmsChildren2 = cmsChildrens?.["cms-children-2"];

  const tagging1 = {
    ...DETAIL_CARD_CONTENT_TAGGING_INFO,
    componentContent: detailCardButton1?.label || "Lo quiero",
    ...detailCardButton1?.taggingInfo,
  };

  const tagging2 = {
    ...DETAIL_CARD_CONTENT_TAGGING_INFO,
    componentContent: detailCardButton2?.label,
    ...detailCardButton2?.taggingInfo,
  };

  const priceEmpty =
    [
      detailCardPrice1,
      detailCardPrice2,
      detailCardText3,
      detailCardText4,
    ].every(checkIfEmpty) && !cmsHandler;

  if (layout === "small" && detailCardText2?.text) {
    detailCardText2.text = truncateText(detailCardText2.text);
  }

  return (
    <div
      className={`card-content layout--${layout} ${className}`}
      style={{ backgroundColor }}
    >
      {shadow && shadow !== "none" && (
        <div className={`shadow${shadow === "gradient" ? "-gradient" : ""}`} />
      )}

      {detailCardImage1 && (detailCardImage1.src || cmsHandler) && (
        <div className={`image-gap${imageSize ? ` size-${imageSize}` : ""}`}>
          <Image
            {...detailCardImage1}
            objectFit="cover"
            cmsHandler={cmsHandler}
          />
        </div>
      )}

      <div
        className={`content${
          cmsHandler && imageSize === "full" ? " content-resize" : ""
        }`}
      >
        {detailCardTitle && (
          <DetailCardTitle
            detailCardTitleProps={detailCardTitle}
            cmsHandler={cmsHandler}
          />
        )}

        <div className="text-info-container">
          <TextInfo
            {...detailCardText1}
            className="description-gap"
            cmsHandler={cmsHandler}
          />
          {cmsChildren1 && (
            <div className="children1-container">{cmsChildren1}</div>
          )}
          <TextInfo
            {...detailCardText2}
            className="description-gap"
            cmsHandler={cmsHandler}
          />
        </div>

        {cmsChildren2 && (
          <div className="children2-container">{cmsChildren2}</div>
        )}

        {detailCardIconList1 && (
          <div className="icon-list-wrapper">
            <DetailCardIconList {...detailCardIconList1} />
          </div>
        )}
      </div>

      <div className="content">
        <div className="card-price-content">
          {!priceEmpty && (
            <div className="price-content">
              <Label {...detailCardLabel1} cmsHandler={cmsHandler} />
              <TextInfo
                {...detailCardText3}
                className="pre-price"
                cmsHandler={cmsHandler}
              />
              <div className="price-inline">
                <Price {...detailCardPrice2} cmsHandler={cmsHandler} />
                <Price {...detailCardPrice1} cmsHandler={cmsHandler} />
              </div>
              <TextInfo
                {...detailCardText4}
                className="post-price"
                cmsHandler={cmsHandler}
              />
            </div>
          )}

          <div className="action-buttons">
            <Button
              {...detailCardButton1}
              taggingInfo={tagging1}
              cmsHandler={cmsHandler}
            />
            {detailCardButton2?.label && (
              <CustomLink
                {...detailCardButton2}
                taggingInfo={tagging2}
                cmsHandler={cmsHandler}
                id="detail-card-button-2"
                dataAbt="ver-detalle"
              >
                <TextInfo
                  text={detailCardButton2?.label}
                  color={TEXT_COLOR.MOVISTAR_BLUE}
                  fontType={TEXT_FONT_WEIGHT.REGULAR}
                  size={TEXT_SIZE.CAPTION3}
                  cmsHandler={cmsHandler}
                  className="hover"
                />
              </CustomLink>
            )}
          </div>
        </div>
      </div>

      <div className={loaded ? "skeleton loaded" : "skeleton"} />

      <style jsx>
        {`
          .card-content {
            display: flex;
            flex-direction: column;
            min-width: 316px;
            width: min(20.9vw, 352px);
            height: 100%;
            border-radius: 16px;
            overflow: hidden;
            border: 1px solid #ddd;
            justify-content: space-between;
          }

          .image-gap {
            position: relative;
            min-height: 14.9rem;
            aspect-ratio: 4/3;
            width: 100%;
          }

          .image-gap.size-small {
            min-height: 6rem;
            aspect-ratio: 13/3;
          }

          .image-gap.size-full {
            min-height: 100%;
            position: absolute;
          }

          .content {
            padding: 1rem;
            display: flex;
            flex-direction: column;
          }

          .content.content-resize {
            margin-top: 40px;
          }

          .text-info-container {
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          :global(.text-info-container ul) {
            line-height: 1.8 !important;
          }

          :global(.text-info-container li) {
            line-height: 1.5 !important;
          }

          .children1-container {
            margin-bottom: 0.5rem;
            margin-left: 0.5rem;
          }

          .icon-list-wrapper,
          .children2-container,
          .card-price-content {
            width: 100%;
          }

          .icon-list-wrapper {
            display: flex;
            flex-direction: row;
            flex: 1;
          }

          .children2-container {
            display: flex;
            flex-direction: column;
          }

          .card-price-content {
            display: flex;
            flex-direction: column;
            gap: 0;
          }

          .price-inline {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }

          .action-buttons {
            display: flex;
            justify-content: space-between;
            margin-top: 12px;
          }

          .skeleton {
            position: absolute;
            top: 0;
            left: 0;
            border-radius: 16px;
          }

          .skeleton.loaded {
            display: none;
          }

          @media screen and (max-width: 1024px) {
            .card-content {
              width: 20rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CardContent;